import type { LendingRequest, MessageKey } from '@ralph/contracts'
import { formatBytes, formatCount, pluralCategory } from '@/i18n/format.ts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * What the knowledge domain's refusals say, in English (the source of truth AND the type).
 *
 * The namespace is selected from `MessageKey` rather than listed by hand: a key added to
 * `KnowledgePayloads` becomes a missing property HERE, in the file that owes it a sentence,
 * instead of an error at the aggregator that names no domain.
 */
type KnowledgeMessages = Pick<ServerMessages, Extract<MessageKey, `knowledge.${string}`>>

/**
 * The plural forms a refusal needs. `pluralCategory` rather than `n === 1` even in English,
 * where the two agree, because the Portuguese sibling must ask the same question and a rule
 * that is only applied where it changes the answer is a rule nobody copies (D-19).
 */
const _documents = (count: number): string =>
  `${count} ${pluralCategory('en', count) === 'one' ? 'document' : 'documents'}`

/**
 * Where a request already stands, as the refusal for deciding it twice names it.
 *
 * A map over the whole enum rather than the four states that can actually reach the refusal:
 * a sixth status has to be given a word here before it compiles, which is the only thing that
 * keeps `withdrawn` off a screen (LOC-018). The sentence is built around the map so every
 * member reads as a state rather than as a past action -- Portuguese needs the same shape.
 */
const standing: Record<LendingRequest['status'], string> = {
  pending: 'waiting for an answer',
  approved: 'approved',
  declined: 'declined',
  withdrawn: 'withdrawn',
  closed: 'closed'
}

/**
 * This file's own locale, named once.
 *
 * A catalogue entry takes its parameters and nothing else — `describeApiError` looks a key up
 * and calls it, with no formatter bound — so an entry that counts or sizes something has to
 * name the locale it IS. That is not the hardcoded 'en' LOC-020 bans: it is this file's
 * identity, and the pt-BR sibling says 'pt-BR' in the same place for the same reason.
 */
const LOCALE = 'en'

const counted = (n: number, one: string, other: string): string =>
  `${formatCount(LOCALE, n)} ${pluralCategory(LOCALE, n) === 'one' ? one : other}`

/** The tail both capacity refusals end on: what a person can actually do about it. */
const RAISE = 'or ask an administrator to raise the limit.'

export const knowledge: KnowledgeMessages = {
  // --- a base, and who may see or change it ---------------------------------
  'knowledge.base_not_available': () => 'No knowledge base with that name is available to you.',
  'knowledge.only_maintainers_can_change': () => 'Only the people who maintain this knowledge base can change it.',
  'knowledge.only_administrators_list_all': () => 'Only administrators can list every knowledge base.',
  'knowledge.name_taken': ({ name }) =>
    `Something else is already called “${name}”. Knowledge base names are shared across the whole workspace so answers can say where they came from — pick a different one.`,
  'knowledge.nothing_to_change': () => 'There is nothing to change here.',
  'knowledge.owner_group_not_managed': () => 'You can only create knowledge bases for groups you manage.',
  'knowledge.destination_group_not_managed': () => 'You can move a knowledge base only to a group you manage.',
  'knowledge.already_owned_there': () => 'It already belongs there.',
  'knowledge.confirm_base_name': ({ name }) => `Type ${name} to confirm that this knowledge base should be deleted.`,
  'knowledge.access_changed_while_editing': () =>
    'Someone changed who can use this while you were editing. Reload it and try again.',
  'knowledge.ocr_language_unavailable': ({ languages, available }) =>
    `This deployment has no OCR language pack for ${languages}. Available: ${available === '' ? 'none' : available}.`,

  // --- limits a person can act on -------------------------------------------
  'knowledge.base_at_document_limit': ({ documents }) =>
    `This knowledge base already holds ${counted(documents, 'document', 'documents')}, which is as many as it can ` +
    `take. Delete something you no longer need, ${RAISE}`,
  'knowledge.base_out_of_space': ({ limitBytes, usedBytes }) =>
    `There is not enough space left in this knowledge base — it holds up to ${formatBytes(LOCALE, limitBytes)} and ` +
    `${formatBytes(LOCALE, usedBytes)} is already in use. Delete a document you no longer need, or ask an ` +
    'administrator for more room.',

  'knowledge.file_too_large': ({ filename, sizeBytes, limitBytes }) =>
    `“${filename}” is ${formatBytes(LOCALE, sizeBytes)}, and a file can be up to ${formatBytes(LOCALE, limitBytes)}.`,

  // --- documents ------------------------------------------------------------
  'knowledge.choose_a_file': () => 'Choose a file to add.',
  'knowledge.document_not_in_base': () => 'That document is not in this knowledge base.',
  'knowledge.stored_copy_gone': () => 'The stored copy of this document is no longer available. Upload the file again.',

  // --- letting an agent answer from a base, and asking for it ---------------
  'knowledge.agent_does_not_use_base': () =>
    'That agent does not use this knowledge base, so there is nothing to allow.',
  'knowledge.base_already_usable': () => 'You can already use this knowledge base.',
  'knowledge.attach_base_first': () => 'Attach the knowledge base to the agent before asking for it.',
  'knowledge.agent_already_answers_from_base': () => 'This agent already answers from that knowledge base.',
  'knowledge.ask_already_pending': () => 'You already asked, and nobody has answered yet.',
  'knowledge.ask_declined_recently': () => 'They said no to this recently. You can ask again tomorrow.',
  'knowledge.too_many_asks': () => 'You have asked for a lot of knowledge in the last hour. Try again later.',
  'knowledge.request_not_found': () => 'There is no request with that id.',
  'knowledge.no_open_request_of_yours': () => 'There is no open request of yours with that id.',
  'knowledge.request_already_decided': ({ status }) => `This request is already ${standing[status]}.`
}
